import {useStore} from 'zustand';
import {createJSONStorage, persist} from 'zustand/middleware';
import {createStore} from 'zustand/vanilla';
import {KnownCountryCode} from '@cci-shared/features/const/country';
import {DeviceType, DistanceUnitType, RegionType, SpeedUnitType, ThemeType, UserOsType} from '@cci-shared/features/const/environment';
import getDeviceType from '@di-shared/features/environment/getDeviceType';
import {WebviewUtils} from '@shared/webviewUtils';

export interface EnvironmentState {
  sub: string;
  puid: string;
  ccid: string;
  locale: KnownCountryCode;
  region: RegionType;
  lang: string;
  os: UserOsType;
  osVersion: string;
  appVersion: string | null;
  isWebview: boolean;
  theme: ThemeType;
  device: DeviceType;
  speedUnit: SpeedUnitType;
  distanceUnit: DistanceUnitType;
  timezone: string;
  vin: string;

  setTheme(theme: ThemeType): void;

  setVin(vin: string): void;

  setDevice(device: DeviceType): void;

  initEnvironment(appName: string, userAgent: string): void;
}

interface WebviewUtilsEnvironment {
  os: UserOsType;
  osVersion: string;
  appVersion: string;
  region: RegionType;
  lang: string;
  locale: KnownCountryCode;
  theme: ThemeType;
  isWebview: boolean;
  speedUnit: SpeedUnitType;
  distanceUnit: DistanceUnitType;
  timezone: string;
}

const environmentStore = createStore(
  persist<EnvironmentState>(
    (set) => ({
      puid: '',
      ccid: '',
      sub: '',
      locale: RegionType.KR,
      region: RegionType.KR,
      lang: 'ko-KR',
      os: UserOsType.WEB,
      osVersion: '0.0',
      appVersion: null,
      isWebview: true,
      theme: ThemeType.LIGHT,
      device: DeviceType.PC,
      speedUnit: SpeedUnitType.KM_H,
      distanceUnit: DistanceUnitType.KM,
      timezone: '',
      vin: '',

      setVin(vin: string) {
        set({vin});
      },
      setTheme(theme: ThemeType) {
        set({theme});
        updateThemeClass(theme);
      },
      setDevice(device: DeviceType) {
        set({device});
      },
      initEnvironment(appName: string, userAgent: string) {
        // TODO 앱에서 준 값이 없다면? 디폴트 값은 PC 기준 취득한 값을 넣어줘야함!
        const environment = WebviewUtils.getEnvironment({
          appName,
          userAgent,
          locale: 'KR',
          region: import.meta.env.VITE_REGION || RegionType.KR,
          lang: 'ko-KR',
          theme: ThemeType.DARK,
          speedUnit: SpeedUnitType.KM_H,
          distanceUnit: DistanceUnitType.KM,
          timezone: '',
        }) as WebviewUtilsEnvironment;
        const device = getDeviceType();

        set({
          os: environment.os.toUpperCase() as UserOsType,
          appVersion: environment.appVersion,
          osVersion: environment.osVersion,
          region: environment.region.toUpperCase() as RegionType,
          lang: environment.lang,
          locale: environment.locale.toUpperCase() as KnownCountryCode,
          theme: environment.theme.toUpperCase() as ThemeType,
          device,
          isWebview: environment.isWebview,
          speedUnit: environment.speedUnit.toUpperCase() as SpeedUnitType,
          distanceUnit: environment.distanceUnit.toUpperCase() as DistanceUnitType,
          timezone: environment.timezone,
        });

        updateThemeClass(environment.theme);
      },
    }),
    {
      name: 'driving-insight-app-environment',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

function updateThemeClass(theme: ThemeType) {
  document.body.classList.remove(...Object.values(ThemeType).map((theme) => `${theme.toLowerCase()}`));
  document.body.classList.add(`${theme.toLowerCase()}`);
}

export function useEnvironmentStore<SelectedState>(selector: (state: EnvironmentState) => SelectedState) {
  return useStore(environmentStore, selector);
}

export default environmentStore;
